import { useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { ListFilter, Plus } from "lucide-react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { SaveFiltersSelect } from "components/NewAdvancedSearch/SaveFilters/SaveFiltersSelect";
import { FilterCard } from "components/NewAdvancedSearch/FilterCard";
import { CustomFieldFilterCard } from "components/NewAdvancedSearch/CustomFieldFilterCard";
import { buildQueryParams } from "components/NewAdvancedSearch/buildQueryParams";
import { parseQueryParams } from "components/NewAdvancedSearch/parseQueryParams";
import { legacyFilterToQueryParams } from "components/NewAdvancedSearch/legacyFilterToQueryParams";

const makeEmptyRule = (kind = "filter") => ({ field: null, value: null, kind });

// Counts applied filters straight from the URL (works without opening the
// panel). A descriptor is applied when the URL has its key or any `key:suffix`
// param; tags live in one param whose `;`-joined segments each count once.
const countAppliedFilters = (queryParams, filters) => {
  const applied = filters.filter(f => {
    if (f.type === "tag") return false;
    // A grid's own key is never in the URL; it's applied when any of its
    // component flags is present.
    if (f.type === "grid") {
      return (f.components ?? []).some(c => queryParams[c.key] != null);
    }
    // A listFilter is stored under `search:<key>:<uuid>`, not its bare key.
    if (f.type === "listFilter") {
      return Object.keys(queryParams).some(key =>
        key.startsWith(`search:${f.key}:`)
      );
    }
    return Object.keys(queryParams).some(
      key => key === f.key || key.startsWith(`${f.key}:`)
    );
  }).length;

  const tagCount = queryParams.tags
    ? String(queryParams.tags).split(";").filter(Boolean).length
    : 0;

  return applied + tagCount;
};

export function NewAdvancedSearch({
  filters = [],
  queryParams = {},
  tableName,
  onApply,
}) {
  const methods = useForm({
    defaultValues: { rules: [makeEmptyRule()] },
  });
  const { control, handleSubmit, reset } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: "rules" });
  const watchedRules = useWatch({ control, name: "rules" });
  // Tags are exempt from the one-card-per-filter rule: each tag row is its own
  // search group (an AND-combined contains/excludes set), so the picker keeps
  // offering "Tag" even after one is added.
  const usedKeys = (watchedRules ?? [])
    .filter(rule => rule.field?.type !== "tag")
    .map(rule => rule.field?.key)
    .filter(Boolean);

  // The URL the form was last built from. Something other than the panel can
  // change the filters (the Activity Log's quick filters), so the form is
  // rebuilt whenever the URL has moved since, and otherwise keeps an
  // unapplied draft across a close and reopen.
  const [hydratedFrom, setHydratedFrom] = useState(null);

  const regularFilters = filters.filter(f => !f.isCustomField);
  const customFieldFilters = filters.filter(f => f.isCustomField);
  const hasCustomFields = customFieldFilters.length > 0;

  const appliedCount = countAppliedFilters(queryParams, filters);

  const applyFilters = ({ rules }) => {
    onApply?.(buildQueryParams(rules));
  };

  // Rebuild the form from the URL when the panel opens, so a shared or
  // reloaded link shows its active filters. Deferred to open to avoid firing
  // async select resolution on every table render.
  const handleOpenChange = async open => {
    const current = JSON.stringify(queryParams);
    if (!open || hydratedFrom === current) return;
    const rules = await parseQueryParams(queryParams, filters);
    reset({ rules: rules.length ? rules : [makeEmptyRule()] });
    setHydratedFrom(current);
  };

  // Applies a saved filter. New-pattern entries already hold the flat query
  // params; legacy AdvancedSearch entries are flattened from their stored state.
  const handleSelectSavedFilter = async savedFilter => {
    const params = savedFilter.isNewPattern
      ? savedFilter.state
      : legacyFilterToQueryParams(savedFilter.state, filters);

    const rules = await parseQueryParams(params, filters);

    reset({ rules: rules.length ? rules : [makeEmptyRule()] });
    onApply?.(params);
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              aria-label="Filters"
              render={
                <Button
                  variant={appliedCount > 0 ? "secondary" : "outline"}
                  size="icon"
                  className="tw:relative"
                >
                  <ListFilter />
                  {appliedCount > 0 && (
                    <Badge className="tw:absolute tw:-top-0.5 tw:-right-0.5 tw:h-4 tw:min-w-4 tw:justify-center tw:rounded-full tw:px-1 tw:text-[10px] tw:tabular-nums tw:overflow-visible">
                      {appliedCount}
                    </Badge>
                  )}
                </Button>
              }
            />
          }
        />
        <TooltipContent>Filters</TooltipContent>
      </Tooltip>
      <PopoverContent className="tw:w-160" align="end">
        <PopoverHeader>
          <div className="tw:flex tw:justify-between tw:items-center">
            <span className="tw:cn-font-heading tw:text-base tw:font-semibold">
              Filters
            </span>
            <SaveFiltersSelect
              tableName={tableName}
              rules={watchedRules}
              onSelect={handleSelectSavedFilter}
            />
          </div>
        </PopoverHeader>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(applyFilters)}>
            <div className="tw:flex tw:flex-col tw:flex-1 tw:gap-2">
              {fields.map((field, index) =>
                (watchedRules?.[index]?.kind ?? field.kind) ===
                "customField" ? (
                  <CustomFieldFilterCard
                    key={field.id}
                    index={index}
                    fields={customFieldFilters}
                    usedKeys={usedKeys}
                    onRemove={() => remove(index)}
                  />
                ) : (
                  <FilterCard
                    key={field.id}
                    index={index}
                    filters={regularFilters}
                    usedKeys={usedKeys}
                    onRemove={() => remove(index)}
                  />
                )
              )}
            </div>
            <div className="tw:flex tw:justify-between tw:items-center tw:pt-4">
              <div className="tw:flex tw:items-center tw:gap-4">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => append(makeEmptyRule("filter"))}
                >
                  <Plus data-icon="inline-start" />
                  Add filter
                </Button>
                {hasCustomFields && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => append(makeEmptyRule("customField"))}
                  >
                    <Plus data-icon="inline-start" />
                    Add Custom Field
                  </Button>
                )}
              </div>
              <div className="tw:flex tw:gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={() => reset({ rules: [makeEmptyRule()] })}
                >
                  Clear all
                </Button>
                <Button type="submit" size="sm">
                  Apply
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </PopoverContent>
    </Popover>
  );
}
